import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeRange} from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type { DiscountId} from "@@/shared/discounts/discount-id";
import { discountIdFactory } from "@@/shared/discounts/discount-id";
import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type {
    MetaContent} from "@@/shared/meta-content/meta-content";
import {
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import type { OwnerId} from "@@/shared/owner-id";
import { ownerIdZodSchema } from "@@/shared/owner-id";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    DiscountCondition} from "../../conditions/discount-condition";
import {
    discountConditionZodSchema,
} from "../../conditions/discount-condition";

type BogoDiscountType = "BOGO_DISCOUNT";

/**
 * Buy "one" get one free
 * For every x number of items bought gets a value or percentage discount for the items affected
 */
type BogoDiscount = {
    _id: DiscountId;
    _createdAt: IsoAndUnixTimestamp;
    _createdBy: UserId;
    _status: EntityState;
    meta: MetaContent;
    ownerId: OwnerId;
    type: BogoDiscountType;
    validTimeRanges: TimeRange[];
    conditions: DiscountCondition[];
    numberOfItems: number;
};

type FactoryParams = Except<BogoDiscount, "_id" | "_createdAt" | "type">;
const bogoDiscountFactory = (params: FactoryParams): BogoDiscount => {
    return {
        ...params,
        _id: discountIdFactory(),
        _createdAt: nowIsoAndUnix(),
        type: "BOGO_DISCOUNT",
    };
};

const bogoDiscountZodObject = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountId>,
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _status: entityStateZodSchema,
    meta: metaContentZodSchema,
    ownerId: ownerIdZodSchema,
    type: z.literal("BOGO_DISCOUNT"),
    validTimeRanges: z.array(timeRangeZodSchema),
    conditions: z.array(discountConditionZodSchema),
    numberOfItems: z.number().min(1),
});
const bogoDiscountZodSchema: z.ZodSchema<BogoDiscount> = bogoDiscountZodObject;

export { bogoDiscountFactory, bogoDiscountZodObject, bogoDiscountZodSchema };
export type { BogoDiscount, BogoDiscountType };
